import { ReactNode, useEffect, useRef, useState } from 'react';
import { FilterChoice } from './CustomReportService';
import CustomizeViewDrawer, { ColumnSetup, CustomFieldDraft, DrawerTab } from './CustomizeViewDrawer';
import { ExtraFilter } from './mockReportData';
import { ReportDef } from './reportRegistry';

type Props = {
    report: ReportDef;
    extraFilters?: ExtraFilter[];
    extraFilterLabel?: string;
    countText: string;
    sortOptions: string[];
    columns: string[];
    footnotes?: string[];
    actions?: 'standard' | 'upload';
    hideStandardFilters?: boolean;
    moreFiltersLabel?: string;
    live?: boolean;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    campusSlot?: ReactNode;
    barFilters?: { key: string; label: string; control?: 'search' | 'institute' | 'pill' }[];
    filterBar?: {
        top: { key: string; label: string; control?: 'institute' | 'pill'; dataType?: string }[];
        report: { key: string; label: string; removable?: boolean; dataType?: string }[];
        more: { key: string; label: string; dataType?: string }[];
        onRemove: (key: string) => void;
        choices?: Record<string, FilterChoice[]>;
        selected?: Record<string, string[]>;
        onToggle?: (key: string, value: string) => void;
        onCommit?: (key: string, value: string) => void;
        onSuggest?: (key: string, q: string) => Promise<FilterChoice[]>;
    };
    columnSetup?: ColumnSetup;
    banner?: ReactNode;
    onExport?: () => void;
    exportBusy?: boolean;
    hideCustomField?: boolean;
    hideHeading?: boolean;
    children: ReactNode;
};

function SuggestFilter({
    field,
    variant,
    removable,
    selected,
    onRemove,
    onCommit,
    onSuggest,
}: {
    field: { key: string; label: string };
    variant: 'cr-pill' | 'cr-chip';
    removable: boolean;
    selected: string[];
    onRemove?: (key: string) => void;
    onCommit?: (key: string, value: string) => void;
    onSuggest?: (key: string, q: string) => Promise<FilterChoice[]>;
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(selected[0] || '');
    const [suggestions, setSuggestions] = useState<FilterChoice[]>([]);
    const [busy, setBusy] = useState(false);
    const box = useRef<HTMLSpanElement | null>(null);
    const current = selected[0] || '';
    const text = current ? `${field.label}: ${current}` : field.label;

    useEffect(() => {
        if (!open) return;
        setDraft(current);
    }, [open, current]);

    useEffect(() => {
        if (!open) return;
        const close = (event: MouseEvent) => {
            if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const term = draft.trim();
        if (term.length < 2) {
            setSuggestions([]);
            setBusy(false);
            return;
        }
        let cancelled = false;
        const timer = window.setTimeout(() => {
            setBusy(true);
            const request = onSuggest?.(field.key, term) ?? Promise.resolve([]);
            request
                .then((list) => {
                    if (!cancelled) setSuggestions(list);
                })
                .catch(() => {
                    if (!cancelled) setSuggestions([]);
                })
                .finally(() => {
                    if (!cancelled) setBusy(false);
                });
        }, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [draft, open, field.key, onSuggest]);

    const commit = (value: string) => {
        onCommit?.(field.key, value.trim());
        setOpen(false);
    };

    return (
        <span className="cr-filter-menu" key={field.key} ref={box}>
            <span className={`${variant}${current ? ' has-value' : ''}`}>
                <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                    {text} <i className="bi bi-chevron-down"></i>
                </button>
                {removable && (
                    <button type="button" aria-label={`Remove ${field.label}`} onClick={() => onRemove?.(field.key)}>
                        <i className="bi bi-x"></i>
                    </button>
                )}
            </span>
            {open && (
                <div className="cr-filter-drop cr-suggest">
                    <label className="cr-filter-search">
                        <i className="bi bi-search"></i>
                        <input
                            autoFocus
                            placeholder={`Type ${field.label.toLowerCase()}`}
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    commit(draft);
                                }
                            }}
                        />
                    </label>
                    {draft.trim().length < 2 && <span className="cr-hint">Type at least 2 letters</span>}
                    {draft.trim().length >= 2 && busy && <span className="cr-hint">Searching…</span>}
                    {draft.trim().length >= 2 && !busy && suggestions.length === 0 && (
                        <span className="cr-hint">No matching names. Press Enter to filter by this text.</span>
                    )}
                    {suggestions.map((choice) => (
                        <button key={choice.value} type="button" className="cr-suggest-item" onClick={() => commit(choice.value)}>
                            {choice.label}
                        </button>
                    ))}
                </div>
            )}
        </span>
    );
}

const ReportChrome = ({
    report,
    extraFilters,
    extraFilterLabel = 'This report:',
    countText,
    sortOptions,
    columns,
    footnotes,
    actions = 'standard',
    hideStandardFilters = false,
    moreFiltersLabel = 'More Filters',
    live = false,
    searchValue = '',
    onSearchChange,
    campusSlot,
    barFilters,
    filterBar,
    columnSetup,
    banner,
    onExport,
    exportBusy = false,
    hideCustomField = false,
    hideHeading = false,
    children,
}: Props) => {
    const [drawerTab, setDrawerTab] = useState<DrawerTab | null>(null);
    const [customFields, setCustomFields] = useState<CustomFieldDraft[]>([]);
    const [moreOpen, setMoreOpen] = useState(false);
    const [openFilter, setOpenFilter] = useState<string | null>(null);
    const [menuQuery, setMenuQuery] = useState('');
    const openMenu = useRef<HTMLSpanElement | null>(null);
    const openDrawer = (tab: DrawerTab) => setDrawerTab(tab);

    useEffect(() => {
        if (!openFilter) return;
        const close = (event: MouseEvent) => {
            if (openMenu.current && !openMenu.current.contains(event.target as Node)) setOpenFilter(null);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [openFilter]);

    const valueMenu = (
        field: { key: string; label: string; dataType?: string },
        variant: 'cr-pill' | 'cr-chip',
        removable: boolean
    ) => {
        const dataType = field.dataType || 'SELECT';
        const choices = dataType === 'BOOLEAN'
            ? [
                  { value: '1', label: 'Yes' },
                  { value: '0', label: 'No' },
              ]
            : filterBar?.choices?.[field.key] || [];
        const selected = filterBar?.selected?.[field.key] || [];
        const names = selected.map((value) => choices.find((choice) => choice.value === value)?.label || value);
        const text = names.length === 0 ? field.label : names.length === 1 ? `${field.label}: ${names[0]}` : `${field.label} (${names.length})`;
        const open = openFilter === field.key;
        const needle = menuQuery.trim().toLowerCase();
        const visible = choices.filter((choice) => {
            if (!needle) return true;
            return choice.label.toLowerCase().includes(needle) || choice.value.toLowerCase().includes(needle);
        });
        if (dataType === 'SUGGEST') {
            return (
                <SuggestFilter
                    key={field.key}
                    field={field}
                    variant={variant}
                    removable={removable}
                    selected={filterBar?.selected?.[field.key] || []}
                    onRemove={filterBar?.onRemove}
                    onCommit={filterBar?.onCommit}
                    onSuggest={filterBar?.onSuggest}
                />
            );
        }
        return (
            <span className="cr-filter-menu" key={field.key} ref={open ? openMenu : undefined}>
                <span className={`${variant}${names.length ? ' has-value' : ''}`}>
                    <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => {
                            setMenuQuery('');
                            setOpenFilter(open ? null : field.key);
                        }}>
                        {text} <i className="bi bi-chevron-down"></i>
                    </button>
                    {removable && (
                        <button type="button" aria-label={`Remove ${field.label}`} onClick={() => filterBar?.onRemove(field.key)}>
                            <i className="bi bi-x"></i>
                        </button>
                    )}
                </span>
                {open && (
                    <div className="cr-filter-drop">
                        {dataType === 'TEXT' && (
                            <label className="cr-filter-search">
                                <i className="bi bi-search"></i>
                                <input
                                    autoFocus
                                    placeholder={`Search ${field.label}`}
                                    value={menuQuery}
                                    onChange={(event) => setMenuQuery(event.target.value)}
                                />
                            </label>
                        )}
                        {dataType === 'NUMBER' && (
                            <label className="cr-filter-search">
                                <input
                                    autoFocus
                                    type="number"
                                    placeholder="Number"
                                    value={menuQuery}
                                    onChange={(event) => setMenuQuery(event.target.value)}
                                />
                            </label>
                        )}
                        {dataType === 'DATE' && (
                            <label className="cr-filter-search">
                                <input
                                    autoFocus
                                    type="date"
                                    value={menuQuery}
                                    onChange={(event) => setMenuQuery(event.target.value)}
                                />
                            </label>
                        )}
                        {visible.length === 0 && <span className="cr-hint">No values in this report yet.</span>}
                        {visible.map((choice) => (
                            <label key={choice.value}>
                                <input
                                    type="checkbox"
                                    checked={selected.includes(choice.value)}
                                    onChange={() => filterBar?.onToggle?.(field.key, choice.value)}
                                />
                                <span>{choice.label}</span>
                            </label>
                        ))}
                    </div>
                )}
            </span>
        );
    };

    return (
        <section className="cr-rpt">
            <div className="cr-rpt-head">
                {!hideHeading && (
                    <div>
                        <h2>
                            <i className={`bi ${report.icon} me-2`}></i>
                            {report.label}
                        </h2>
                        <p className="cr-rpt-desc">{report.description}</p>
                    </div>
                )}
                <div className="cr-rpt-actions">
                    {actions === 'upload' ? (
                        <>
                            <button type="button" className="cr-btn cr-btn-outline">
                                <i className="bi bi-file-earmark-arrow-down me-1"></i>Download templates
                            </button>
                            <button type="button" className="cr-btn cr-btn-navy">
                                <i className="bi bi-cloud-upload me-1"></i>New Upload
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="cr-btn cr-btn-ghost" onClick={() => openDrawer('columns')}>
                                <i className="bi bi-layout-three-columns me-1"></i>Columns
                            </button>
                            <button type="button" className="cr-btn cr-btn-ghost" onClick={() => openDrawer('filters')}>
                                <i className="bi bi-funnel me-1"></i>Filters
                            </button>
                            {!hideCustomField && (
                                <button type="button" className="cr-btn cr-btn-outline" onClick={() => openDrawer('custom')}>
                                    <i className="bi bi-plus-lg me-1"></i>Custom Field
                                </button>
                            )}
                            <button type="button" className="cr-btn cr-btn-navy" onClick={onExport} disabled={exportBusy}>
                                <i className="bi bi-download me-1"></i>
                                {exportBusy ? 'Exporting…' : 'Export'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="cr-filter-bar">
                {!hideStandardFilters && (
                    <div className="cr-filter-row">
                        {live && filterBar ? (
                            <>
                                <div className="cr-search">
                                    <i className="bi bi-search"></i>
                                    <input
                                        placeholder="Name, UUID, email"
                                        value={searchValue}
                                        onChange={(event) => onSearchChange?.(event.target.value)}
                                    />
                                </div>
                                {filterBar.top.map((field) =>
                                    field.control === 'institute' ? (
                                        <span key={field.key}>{campusSlot}</span>
                                    ) : (
                                        valueMenu(field, 'cr-pill', false)
                                    )
                                )}
                                <span className="cr-more-wrap">
                                    <button type="button" className="cr-more" onClick={() => setMoreOpen((open) => !open)}>
                                        More Filters{filterBar.more.length ? ` (${filterBar.more.length})` : ''}{' '}
                                        <i className="bi bi-chevron-down"></i>
                                    </button>
                                    {moreOpen && (
                                        <div className="cr-more-menu">
                                            {filterBar.more.length === 0 && <span className="cr-hint">Nothing tucked away.</span>}
                                            {filterBar.more.map((field) => valueMenu(field, 'cr-pill', true))}
                                        </div>
                                    )}
                                </span>
                            </>
                        ) : live && barFilters ? (
                            barFilters.map((field) => {
                                if (field.control === 'search') {
                                    return (
                                        <div className="cr-search" key={field.key}>
                                            <i className="bi bi-search"></i>
                                            <input
                                                placeholder={field.label}
                                                value={searchValue}
                                                onChange={(event) => onSearchChange?.(event.target.value)}
                                            />
                                        </div>
                                    );
                                }
                                if (field.control === 'institute') return <span key={field.key}>{campusSlot}</span>;
                                return (
                                    <span className="cr-pill" key={field.key}>
                                        {field.label}
                                    </span>
                                );
                            })
                        ) : (
                            <>
                                <div className="cr-search">
                                    <i className="bi bi-search"></i>
                                    <input placeholder="Name, UUID, email…" readOnly />
                                </div>
                                <span className="cr-pill">Campus</span>
                                <span className="cr-pill">Program</span>
                                <span className="cr-pill">Batch Year</span>
                                <span className="cr-pill">Profile Status</span>
                                <button type="button" className="cr-more" onClick={() => openDrawer('filters')}>
                                    {moreFiltersLabel} <i className="bi bi-chevron-down"></i>
                                </button>
                            </>
                        )}
                    </div>
                )}
                {live && filterBar && (
                    <div className="cr-filter-row cr-filter-row-sub">
                        <span className="cr-strip-label">This report:</span>
                        {filterBar.report.map((field) => valueMenu(field, 'cr-chip', field.removable !== false))}
                        <button type="button" className="cr-chip cr-chip-add" onClick={() => openDrawer('filters')}>
                            <i className="bi bi-plus-lg"></i> Add filter
                        </button>
                    </div>
                )}
                {extraFilters && extraFilters.length > 0 && (
                    <div className={`cr-filter-row${hideStandardFilters ? '' : ' cr-filter-row-sub'}`}>
                        {!hideStandardFilters && <span className="cr-strip-label">{extraFilterLabel}</span>}
                        {hideStandardFilters && (
                            <div className="cr-search">
                                <i className="bi bi-search"></i>
                                <input placeholder="File name, uploaded by…" readOnly />
                            </div>
                        )}
                        {extraFilters.map((f) => (
                            <span key={f.label} className={`cr-pill${f.active ? ' active' : ''}`}>
                                {f.active && f.summary ? `${f.label}: ${f.summary}` : f.label}
                                {f.badge != null && <span className="cr-badge">{f.badge}</span>}
                                {f.active ? <i className="bi bi-x"></i> : <i className="bi bi-chevron-down"></i>}
                            </span>
                        ))}
                        {actions !== 'upload' && (
                            <button type="button" className="cr-chip cr-chip-add" onClick={() => openDrawer('filters')}>
                                <i className="bi bi-plus-lg"></i> Add filter
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="cr-rpt-body">
                {banner}
                {(countText || sortOptions.length > 0) && (
                    <div className="cr-toolbar">
                        {countText && (
                            <div className="cr-count">
                                {countText}
                                {!live && <span className="cr-static-note"> — static HTML</span>}
                            </div>
                        )}
                        {sortOptions.length > 0 && (
                            <div className="cr-sort">
                                <span>Sort:</span>
                                <select defaultValue={sortOptions[0]}>
                                    {sortOptions.map((o) => (
                                        <option key={o}>{o}</option>
                                    ))}
                                </select>
                                <select defaultValue="Show 20">
                                    <option>Show 20</option>
                                    <option>Show 50</option>
                                    <option>Show 100</option>
                                </select>
                            </div>
                        )}
                    </div>
                )}

                <div className="table-responsive">
                    <table className="cr-table">
                        <thead>
                            <tr>
                                {actions !== 'upload' && (
                                    <th style={{ width: 30 }}>
                                        <input type="checkbox" className="form-check-input" readOnly />
                                    </th>
                                )}
                                {columns.map((c) => (
                                    <th key={c}>{c}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>{children}</tbody>
                    </table>
                </div>

                {footnotes && footnotes.length > 0 && (
                    <div className="cr-footnote">
                        {footnotes.map((f) => (
                            <div key={f}>{f}</div>
                        ))}
                    </div>
                )}

                {actions !== 'upload' && !live && (
                    <div className="cr-pager">
                        <span>First</span>
                        <span>
                            <i className="bi bi-chevron-left"></i>
                        </span>
                        <span className="current">1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>…</span>
                        <span>
                            <i className="bi bi-chevron-right"></i>
                        </span>
                        <span>Last</span>
                    </div>
                )}
            </div>

            {drawerTab && (
                <CustomizeViewDrawer
                    reportLabel={report.label}
                    shownColumns={columns}
                    activeTab={drawerTab}
                    customFields={customFields}
                    columnSetup={columnSetup}
                    onTabChange={setDrawerTab}
                    onClose={() => setDrawerTab(null)}
                    onCreateField={(field) => setCustomFields((prev) => [...prev, field])}
                />
            )}
        </section>
    );
};

export default ReportChrome;
